/**
 * Idioma de las pantallas de acceso (ingreso, olvidé mi contraseña, restablecer).
 * Español por omisión; la persona puede pasar a inglés y queda recordado en el navegador.
 * El sitio web público usa su propio idioma (inglés por omisión): ver `sitio/idioma.js`.
 */
import { create } from 'zustand';

const CLAVE = 'f360-idioma-acceso';
const leer = () => { try { return localStorage.getItem(CLAVE) === 'en' ? 'en' : 'es'; } catch { return 'es'; } };

export const useIdiomaAcceso = create((set) => ({
  idioma: leer(),
  cambiar: (idioma) => { try { localStorage.setItem(CLAVE, idioma); } catch { /* sin almacenamiento */ } set({ idioma }); },
}));

const TEXTOS = {
  es: {
    rotulo: 'Consola financiera',
    titular: 'Facturación y finanzas de su empresa, en un solo lugar.',
    texto: 'Comprobantes electrónicos, gastos, cuentas por cobrar y por pagar, y la contabilidad al día.',
    pie: 'Facturación electrónica y finanzas, de Kuanta Bridge',
    tarjeta: { tipo: 'Factura electrónica', ok: 'Aceptado',
      pasos: [['Creada', 'Cliente, líneas e impuestos'], ['Firmada', 'Con la llave criptográfica de la empresa'], ['Enviada a Hacienda', 'Clave y consecutivo del comprobante'], ['Aceptada', 'Se envía al cliente y se genera el asiento']],
      modulos: ['Facturación', 'Gastos', 'Cuentas por cobrar', 'Cuentas por pagar', 'Contabilidad', 'Reportes'] },
    idioma: 'Idioma',
    ingresar: 'Ingrese a su cuenta', bajada: 'Con el correo que le dio quien administra Finance360 en su empresa.',
    cuenta: 'Cuenta', cuentaAyuda: 'Identificador de la cuenta (slug)',
    correo: 'Correo electrónico', correoEj: 'usted@empresa.com', clave: 'Contraseña',
    mostrar: 'Mostrar contraseña', ocultar: 'Ocultar contraseña', olvido: '¿Olvidó su contraseña?',
    entrar: 'Entrar', entrando: 'Ingresando...', faltan: 'Por favor complete todos los campos',
    faltaCuenta: 'Por favor ingrese el identificador de la cuenta', bienvenido: 'Bienvenido a Finance360',
    olvidoTitulo: '¿Olvidó su contraseña?', olvidoBajada: 'Escriba su correo y le enviamos un enlace para restablecerla.',
    enviar: 'Enviar instrucciones', enviando: 'Enviando...', volver: 'Volver al ingreso', faltaCorreo: 'Ingrese su correo',
    errorConexion: 'Error de conexión. Intente más tarde.', revise: 'Revise su bandeja',
    reviseTexto: (e) => `Si el correo ${e} está registrado, en unos minutos recibirá las instrucciones para restablecer su contraseña.`,
    expira: 'El enlace vence en 1 hora. Revise también la carpeta de spam.',
    validando: 'Validando el enlace...', invalido: 'Enlace inválido o vencido',
    invalidoTexto: 'Este enlace ya se usó, venció (1 hora) o no es válido. Solicite uno nuevo.', pedirOtro: 'Solicitar otro enlace',
    nueva: 'Nueva contraseña', para: 'para', minimo: 'Mínimo 8 caracteres', confirmar: 'Confirmar contraseña',
    escribaDeNuevo: 'Escriba de nuevo', noCoinciden: 'Las contraseñas no coinciden', guardar: 'Guardar contraseña',
    guardando: 'Guardando...', listo: '¡Contraseña restablecida!', redirigiendo: 'Lo llevamos al ingreso...',
    errorRestablecer: 'Error al restablecer la contraseña', mostrarOcultar: 'Mostrar u ocultar contraseña',
  },
  en: {
    rotulo: 'Finance console',
    titular: 'Invoicing and finance for your business, in one place.',
    texto: 'Electronic tax documents, expenses, receivables and payables, and books kept up to date.',
    pie: 'Electronic invoicing and finance, by Kuanta Bridge',
    tarjeta: { tipo: 'Electronic invoice', ok: 'Accepted',
      pasos: [['Created', 'Customer, lines and taxes'], ['Signed', "With the company's digital key"], ['Sent to the tax authority', 'Key and sequence number'], ['Accepted', 'Sent to the customer and posted to the books']],
      modulos: ['Invoicing', 'Expenses', 'Receivables', 'Payables', 'Accounting', 'Reports'] },
    idioma: 'Language',
    ingresar: 'Sign in to your account', bajada: 'With the email your Finance360 administrator gave you.',
    cuenta: 'Account', cuentaAyuda: 'Account identifier (slug)',
    correo: 'Email', correoEj: 'you@company.com', clave: 'Password',
    mostrar: 'Show password', ocultar: 'Hide password', olvido: 'Forgot your password?',
    entrar: 'Sign in', entrando: 'Signing in...', faltan: 'Please fill in every field',
    faltaCuenta: 'Please enter the account identifier', bienvenido: 'Welcome to Finance360',
    olvidoTitulo: 'Forgot your password?', olvidoBajada: "Enter your email and we'll send you a link to reset it.",
    enviar: 'Send instructions', enviando: 'Sending...', volver: 'Back to sign in', faltaCorreo: 'Enter your email',
    errorConexion: 'Connection error. Please try again later.', revise: 'Check your inbox',
    reviseTexto: (e) => `If ${e} is registered, you will receive instructions to reset your password in a few minutes.`,
    expira: 'The link expires in 1 hour. Check your spam folder too.',
    validando: 'Checking the link...', invalido: 'Invalid or expired link',
    invalidoTexto: 'This link was already used, expired (1 hour) or is not valid. Request a new one.', pedirOtro: 'Request a new link',
    nueva: 'New password', para: 'for', minimo: 'At least 8 characters', confirmar: 'Confirm password',
    escribaDeNuevo: 'Type it again', noCoinciden: 'Passwords do not match', guardar: 'Save password',
    guardando: 'Saving...', listo: 'Password reset!', redirigiendo: 'Taking you to sign in...',
    errorRestablecer: 'Could not reset the password', mostrarOcultar: 'Show or hide password',
  },
};

/** Textos del idioma activo, para usar dentro de las pantallas de acceso. */
export function useTextosAcceso() {
  const idioma = useIdiomaAcceso(s => s.idioma);
  return TEXTOS[idioma];
}
